import React from 'react';
import { useTranslation, useEnvironment } from '@wix/yoshi-flow-editor';
import { st, classes } from './SchedulingAgenda.st.css';
import { DailySessions } from './DailySessions/DailySessions';
import { SessionDetails } from './SessionDetails/SessionDetails';
import Divider from './Divider/Divider';
import {
  SchedulingSectionStatus,
  SchedulingSectionViewModel,
} from '../../../../service-page-view-model/scheduling-section-view-model/schedulingSectionViewModel';
import { Loader } from './Loader/Loader';
import { EmptyState } from './EmptyState/EmptyState';
import { useSettings } from '@wix/tpa-settings/react';
import settingsParams from '../../settingsParams';
import { useLoadAllSessions } from './useLoadAllSessions';
import { CurrentLocationProvider } from './useCurrentLocation';

export type SchedulingAgendaProps = {
  viewModel?: SchedulingSectionViewModel;
  currentLocation?: string;
  currentPage?: number;
  pageSize?: number;
};
export const SchedulingAgenda: React.FC<SchedulingAgendaProps> = ({
  viewModel,
  currentLocation,
  currentPage = 0,
  pageSize = 10,
}) => {
  const settings = useSettings();
  const { t } = useTranslation();
  const { isRTL } = useEnvironment();

  const multiDaySessionCount =
    viewModel?.multiDaySessionsViewModel?.length || 0;
  const { numberOfVisibleSessions, LoadAllSessions } = useLoadAllSessions({
    defaultValue: settingsParams.sessionsPerPage,
    totalNumberOfSessions: multiDaySessionCount,
  });

  const dayGroups = viewModel?.schedulingDaysViewModel ?? [];

  const componentByStatus = () => {
    switch (viewModel?.status) {
      case SchedulingSectionStatus.LOADING:
        return <Loader />;
      case SchedulingSectionStatus.EMPTY:
        return emptyState();
      case SchedulingSectionStatus.FAILED:
        return (
          <EmptyState
            message={t('app.widget.uou-messages.scheduling-error')}
            dataHook="error-message"
          />
        );
      case SchedulingSectionStatus.SUCCESS:
        return paginatedSessions();
      default:
        return <Loader />;
    }
  };

  const emptyState = () => {
    const emptyStateMessage = settings.get(
      settingsParams.scheduleEmptyStateText,
    );
    return <EmptyState message={emptyStateMessage} dataHook="empty-state" />;
  };

  const multiDaySessions = () => {
    const sessions = viewModel?.multiDaySessionsViewModel;
    if (!sessions?.length) {
      return null;
    }
    const visibleSessions = sessions.slice(0, numberOfVisibleSessions);
    return (
      <>
        <ul className={classes.list} data-hook="multi-day-sessions">
          {visibleSessions.map((session, index) => (
            <React.Fragment key={index}>
              <li data-hook="multi-day-session">
                <SessionDetails {...session} />
              </li>
              {index !== visibleSessions.length - 1 && (
                <li role="separator">
                  <Divider />
                </li>
              )}
            </React.Fragment>
          ))}
        </ul>
        <LoadAllSessions />
      </>
    );
  };

  const paginatedSessions = () => {
    const startIndex = currentPage * pageSize;
    const visibleDayGroups = dayGroups.slice(startIndex, startIndex + pageSize);

    return (
      <>
        {multiDaySessions()}
        <ul className={classes.list}>
          {visibleDayGroups.map((scheduleDay, index) => (
            <React.Fragment key={`${scheduleDay.date}-${index}`}>
              <DailySessions viewModel={scheduleDay} />
              {index !== visibleDayGroups.length - 1 ? <Divider /> : null}
            </React.Fragment>
          ))}
        </ul>
      </>
    );
  };

  return (
    <CurrentLocationProvider value={currentLocation}>
      <div
        className={st(classes.root, {
          isRTL,
        })}
        data-hook="scheduling-agenda"
      >
        {componentByStatus()}
      </div>
    </CurrentLocationProvider>
  );
};
